import { getLegoColor, normalizeColorIndex } from "../constants/legoColors";
import type { LayerData, OptimizedBrick } from "./brickOptimizer";
import { LEGO_BRICK_TYPES } from "./brickOptimizer/brickTypes";

const BRICK_1X1 = LEGO_BRICK_TYPES[0]; // "1x1" is first in the library

/**
 * Depth (3D) mode: derive stacked brick layers from a single mosaic grid.
 *
 * Each cell's height comes from the matched LEGO color's perceptual
 * luminance, quantized into `bands` brightness bands (brighter = higher).
 * A column of band b is built solid: bricks in layers 0..b. Absent cells
 * (White / too bright — the same skip rule as the flat build) stay empty.
 */
export const DEPTH_BANDS = 4;

// White reads as "no brick", matching the flat 2D path
const LUMA_SKIP_THRESHOLD = 240;

export const luminanceOf = (colorIndex: number): number => {
  const c = getLegoColor(colorIndex);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
};

export const isAbsentCell = (colorIndex: number): boolean =>
  luminanceOf(colorIndex) > LUMA_SKIP_THRESHOLD ||
  getLegoColor(colorIndex).name === "White";

/** Per-cell band index 0 (base) .. bands-1 (top); -1 = absent. */
export const buildDepthGrid = (
  pixels: number[][],
  width: number,
  height: number,
  bands: number = DEPTH_BANDS,
): Int16Array => {
  const grid = new Int16Array(width * height).fill(-1);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const colorIndex = normalizeColorIndex(pixels[y][x]);
      if (isAbsentCell(colorIndex)) continue;
      const luma = luminanceOf(colorIndex);
      const band = Math.min(bands - 1, Math.floor((luma / 256) * bands));
      grid[y * width + x] = band;
    }
  }
  return grid;
};

/**
 * Build solid stacked layers from a mosaic grid. Layer 0 is the base;
 * each cell appears in every layer up to its band so the build is solid.
 * Empty bands are dropped (layer numbering / z stays band-accurate).
 */
export const buildDepthLayers = (
  pixels: number[][],
  width: number,
  height: number,
  bands: number = DEPTH_BANDS,
): LayerData[] => {
  const depthGrid = buildDepthGrid(pixels, width, height, bands);
  const perBand: OptimizedBrick[][] = Array.from({ length: bands }, () => []);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const band = depthGrid[y * width + x];
      if (band < 0) continue;
      const colorIndex = normalizeColorIndex(pixels[y][x]);
      for (let layer = 0; layer <= band; layer++) {
        perBand[layer].push({
          x,
          y,
          colorIndex,
          colorName: getLegoColor(colorIndex).name,
          brickType: BRICK_1X1,
          z: layer,
          rotation: 0,
        });
      }
    }
  }

  return perBand
    .map((bricks, layer) => ({
      layer,
      bricks,
      brickCount: bricks.length,
      colorUsage: {},
    }))
    .filter((layer) => layer.brickCount > 0);
};
